import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2, Undo2, ShieldAlert } from "lucide-react";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground";

const AREA_META = {
  achievement: { desc: "Daily LETBR / achievement uploads, MTD running state, upload history and the daily compliance log." },
  plans: { desc: "All monthly plans (drafts, submitted, approved) and their revision history." },
  market: { desc: "Vahan market-share snapshots (MTD/YTD), monthly series, daily deltas and upload history. RTO geography, brands and targets are kept." },
  manpower: { desc: "HR manpower rows for every branch and HR upload history. Branch list and department categories are kept." },
  aftersales: { desc: "After-Sales DMS feed: GST billing lines, RO list, warranty claims, vehicle sales, upload history and the daily compliance log. Workshops and service zones are kept." },
};

function fmtWhen(s) {
  return s ? s.slice(0, 16).replace("T", " ") : "";
}

function EraseCard({ areaKey, area, onErased }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const erase = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/data/erase", { area: areaKey, confirm: typed });
      toast.success(`${area.label}: ${data.total.toLocaleString()} records erased`);
      setOpen(false);
      setTyped("");
      onErased();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border bg-card rounded-md p-4 flex flex-col gap-3" data-testid={`erase-card-${areaKey}`}>
      <div>
        <div className="font-heading text-sm font-semibold">{area.label}</div>
        <p className="text-xs text-muted-foreground mt-1">{AREA_META[areaKey]?.desc}</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(area.counts).map(([c, n]) => (
          <span key={c} className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">{c}: <b className="text-foreground">{n.toLocaleString()}</b></span>
        ))}
      </div>
      <div className="mt-auto flex items-center justify-between">
        <span className="text-xs text-muted-foreground" data-testid={`erase-total-${areaKey}`}>{area.total.toLocaleString()} records</span>
        <Button variant="destructive" size="sm" disabled={area.total === 0} onClick={() => setOpen(true)} data-testid={`erase-btn-${areaKey}`}>
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Erase
        </Button>
      </div>
      <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTyped(""); }}>
        <AlertDialogContent data-testid={`erase-dialog-${areaKey}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-red-600" /> Erase all {area.label.toLowerCase()} data?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes {area.total.toLocaleString()} records and cannot be undone. Users, dealers, districts, models, sources and settings are not affected.
              Type <b>ERASE</b> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="ERASE" className="font-mono" data-testid={`erase-confirm-input-${areaKey}`} />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`erase-cancel-${areaKey}`}>Cancel</AlertDialogCancel>
            <Button variant="destructive" disabled={typed.trim() !== "ERASE" || busy} onClick={erase} data-testid={`erase-confirm-btn-${areaKey}`}>
              {busy ? "Erasing…" : "Erase permanently"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = status === "committed" ? "bg-emerald-50 text-emerald-800" : status === "rolled_back" ? "bg-secondary text-muted-foreground line-through" : "bg-amber-50 text-amber-800";
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{status === "rolled_back" ? "rolled back" : status}</span>;
}

function RollbackTable({ kind, title, rows, columns, onDone }) {
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const rollback = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/data/rollback/${kind}/${target.id}`);
      toast.success(`Rolled back ${target.file_name}: ${data.rows_removed.toLocaleString()} rows removed`);
      setTarget(null);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid={`rollback-table-${kind}`}>
      <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">{title}</h3></div>
      <table className="w-full text-xs">
        <thead className="border-b"><tr>{[...columns.map((c) => c.h), "Live rows", "Status", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className={`border-b last:border-0 ${r.status === "rolled_back" ? "opacity-60" : ""}`} data-testid={`rollback-row-${kind}-${r.id}`}>
              {columns.map((c) => <td key={c.h} className={`px-3 py-1.5 ${c.cls || ""}`}>{c.render(r)}</td>)}
              <td className="px-3 py-1.5 num">{r.live_rows.toLocaleString()}</td>
              <td className="px-3 py-1.5"><StatusBadge status={r.status} /></td>
              <td className="px-3 py-1.5 text-right">
                {r.can_rollback ? (
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setTarget(r)} data-testid={`rollback-btn-${kind}-${r.id}`}>
                    <Undo2 className="h-3 w-3 mr-1" /> Roll back
                  </Button>
                ) : (
                  <span className="text-[10px] text-muted-foreground max-w-[220px] inline-block text-left" title={r.reason} data-testid={`rollback-reason-${kind}-${r.id}`}>{r.reason}</span>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={columns.length + 3} className="px-3 py-6 text-center text-muted-foreground">No uploads</td></tr>}
        </tbody>
      </table>
      <AlertDialog open={!!target} onOpenChange={(v) => { if (!v) setTarget(null); }}>
        <AlertDialogContent data-testid={`rollback-dialog-${kind}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Roll back this upload?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{target?.file_name}</b> uploaded {fmtWhen(target?.created_at)} by {target?.by}. Its {target?.live_rows?.toLocaleString()} rows will be removed
              {kind === "dpc" && target?.mode === "mtd" ? " and the MTD running totals will be restored to the previous upload." : kind === "mis" ? " and monthly series/daily changes will be rebuilt." : "."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`rollback-cancel-${kind}`}>Cancel</AlertDialogCancel>
            <Button variant="destructive" disabled={busy} onClick={rollback} data-testid={`rollback-confirm-btn-${kind}`}>{busy ? "Rolling back…" : "Roll back"}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const DPC_COLS = [
  { h: "When", cls: "text-muted-foreground whitespace-nowrap", render: (r) => fmtWhen(r.created_at) },
  { h: "File", cls: "font-medium", render: (r) => r.file_name },
  { h: "Mode", cls: "uppercase", render: (r) => r.mode },
  { h: "Dates", cls: "text-muted-foreground whitespace-nowrap", render: (r) => r.dates.join(", ") },
  { h: "By", cls: "text-muted-foreground", render: (r) => r.by },
  { h: "Rows", cls: "num", render: (r) => r.rows_ok },
];

const MIS_COLS = [
  { h: "When", cls: "text-muted-foreground whitespace-nowrap", render: (r) => fmtWhen(r.created_at) },
  { h: "File", cls: "font-medium", render: (r) => r.file_name },
  { h: "Sheets", cls: "text-muted-foreground", render: (r) => r.sheets.length > 3 ? `${r.sheets.slice(0, 3).join(", ")} +${r.sheets.length - 3}` : r.sheets.join(", ") },
  { h: "By", cls: "text-muted-foreground", render: (r) => r.by },
  { h: "Rows", cls: "num", render: (r) => r.rows.toLocaleString() },
];

export function DataAdmin() {
  const [stats, setStats] = useState(null);
  const [uploads, setUploads] = useState({ dpc: [], mis: [] });

  const load = useCallback(() => {
    api.get("/admin/data/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/data/uploads").then(({ data }) => setUploads(data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-8" data-testid="admin-data">
      <section className="space-y-3">
        <div>
          <h2 className="font-heading text-base font-semibold">Erase data by area</h2>
          <p className="text-xs text-muted-foreground">Wipe one area to start fresh. Master data (users, dealers, districts, models, sources, settings) is never touched.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {stats && Object.entries(stats).map(([k, a]) => <EraseCard key={k} areaKey={k} area={a} onErased={load} />)}
        </div>
      </section>
      <section className="space-y-3">
        <div>
          <h2 className="font-heading text-base font-semibold">Roll back an upload</h2>
          <p className="text-xs text-muted-foreground">Undo a single file. Only the latest upload for a given day/month can be rolled back — newer uploads build on older ones, so undo them newest-first.</p>
        </div>
        <RollbackTable kind="dpc" title="DPC achievement uploads (LETBR / daily)" rows={uploads.dpc} columns={DPC_COLS} onDone={load} />
        <RollbackTable kind="mis" title="Market share uploads (Vahan workbook)" rows={uploads.mis} columns={MIS_COLS} onDone={load} />
      </section>
    </div>
  );
}
